import { useEffect, useState } from "react";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Tooltip from "@mui/material/Tooltip"
import dayjs from "dayjs";
import "./DateRange.css";

const DateRange = ({ defaultDays = 10, startTime = null, endTime = null, onChange = () => { } }) => {
    const nowFloored = dayjs();
    const parseOrDefault = (val, fallback) =>
        val ? dayjs(val) : fallback;

    const [startDate, setStartDate] = useState(() =>
        parseOrDefault(startTime, nowFloored.subtract(defaultDays, "day"))
    );
    const [endDate, setEndDate] = useState(() =>
        parseOrDefault(endTime, nowFloored)
    );
    const [rangeError, setRangeError] = useState("");

    const computeRangeError = (s, e) => {
        if (!s || !e) return "Please select both start and end times.";
        const now = dayjs();
        if (dayjs(s).isAfter(now)) return "Start time cannot be in the future.";
        if (dayjs(e).isAfter(now)) return "End time cannot be in the future.";
        if (dayjs(e).isBefore(s)) return "End time must be after the start time.";
        return "";
    };

    useEffect(() => {
        const err = computeRangeError(startDate, endDate);
        setRangeError(err);
        onChange({ startDate, endDate, rangeError: err });
    }, [startDate, endDate]);

    useEffect(() => {
        const parsed = parseOrDefault(startTime, nowFloored.subtract(defaultDays, "day"));
        if (!dayjs(parsed).isSame(startDate)) {
            setStartDate(parsed);
        }
    }, [startTime, defaultDays]);

    useEffect(() => {
        const parsed = parseOrDefault(endTime, nowFloored);
        if (!dayjs(parsed).isSame(endDate)) {
            setEndDate(parsed);
        }
    }, [endTime]);

    const customSlotProps = {
        textField: {
            size: "small",
            error: !!rangeError,
            helperText: !!rangeError ? "" : undefined,
            InputProps: {
                classes: {
                    root: "custom-dt-input-root"
                },
                inputProps: {
                    title: rangeError || "",
                },
            },
            InputLabelProps: { className: !!rangeError ? "custom-dt-label error" : "custom-dt-label" }

        },
        popper: {
            className: "custom-popper",
            placement: "bottom-start",
            container: typeof document !== "undefined" ? document.body : undefined,
            modifiers: [
                {
                    name: "flip",
                    options: { fallbackPlacements: ["bottom-start", "right", "left"] },
                },
                {
                    name: "preventOverflow",
                    options: { rootBoundary: "viewport", altAxis: true, },
                },
            ],
        },
    };


    return (
        <div className="date-range">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Tooltip title={rangeError || ""} disableHoverListener={!rangeError} arrow placement="top" classes={{ tooltip: "date-range-tooltip" }}>
                    <span>
                        <DateTimePicker
                            label="Start Time"
                            value={startDate}
                            onChange={(val) => setStartDate(val ? val.second(0).millisecond(0) : null)}
                            disableFuture
                            maxDateTime={endDate || dayjs()}
                            timeSteps={{ minutes: 1 }}
                            slotProps={customSlotProps}
                        />
                    </span>
                </Tooltip>

                <Tooltip title={rangeError || ""} disableHoverListener={!rangeError} arrow placement="top">
                    <span>
                        <DateTimePicker
                            label="End Time"
                            value={endDate}
                            onChange={(val) => setEndDate(val ? val.second(0).millisecond(0) : null)}
                            disableFuture
                            minDateTime={startDate || undefined}
                            timeSteps={{ minutes: 1 }}
                            slotProps={customSlotProps}
                        />
                    </span>
                </Tooltip>
            </LocalizationProvider>
        </div>
    );
};

export default DateRange;
